import React, { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { Icon } from '../components/Icon';
import { Header } from '../components/Header';
import { useTranslation } from 'react-i18next';
import { TransactionsScreen } from './TransactionsScreen';
import { AnalyticsScreen } from './AnalyticsScreen';
import { RecurringTransactionsScreen } from './RecurringTransactionsScreen';
import { LimitsScreen } from './LimitsScreen';
import { CategoriesContent } from './CategoriesScreen';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { apiClient } from '../api/client';
import { Category, PaymentMethod } from '../types';
import { ExportFlow, ExportConfig } from '../components/ExportFlow';
import { generateExcelFile } from '../utils/excelExport';
import { PremiumFeature } from '../components/PremiumFeature';
import { PlaidLink } from '../components/PlaidLink';
import { formatCurrency, getCurrencySymbol } from '../utils/format';
import { Modal } from '../components/Modal';
import './OperationsScreen.css';
import '../screens/AccountsScreen.css';

type TabType = 'transactions' | 'analytics' | 'categories' | 'limits' | 'accounts' | 'recurring';

interface TabConfig {
  id: TabType;
  icon: string;
  labelKey: string;
}

const tabs: TabConfig[] = [
  { id: 'transactions', icon: 'list', labelKey: 'operations.tabs.transactions' },
  { id: 'analytics', icon: 'bar-chart', labelKey: 'operations.tabs.analytics' },
  { id: 'recurring', icon: 'repeat', labelKey: 'operations.tabs.recurring' },
  { id: 'categories', icon: 'folder', labelKey: 'operations.tabs.categories' },
  { id: 'limits', icon: 'alert-circle', labelKey: 'operations.tabs.limits' },
  { id: 'accounts', icon: 'credit_card', labelKey: 'operations.tabs.accounts' },
];

export const OperationsScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('transactions');
  const { t } = useTranslation();
  const { settings } = useAppStore();
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Payment Methods (manual accounts)
  const [manualAccounts, setManualAccounts] = useState<PaymentMethod[]>([]);
  const [newAccountName, setNewAccountName] = useState('');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [editingBalanceId, setEditingBalanceId] = useState<string | null>(null);
  const [editingBalanceValue, setEditingBalanceValue] = useState('');
  
  // Plaid accounts
  const [plaidAccounts, setPlaidAccounts] = useState<any[]>([]);
  const [plaidItems, setPlaidItems] = useState<any[]>([]);
  const [syncingItemId, setSyncingItemId] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectingItemId, setDisconnectingItemId] = useState<string | null>(null);
  const [removingAccountId, setRemovingAccountId] = useState<string | null>(null);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  
  // Get user name from Telegram WebApp
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const userName = tgUser?.first_name || tgUser?.username || 'User';

  // Прокрутка наверх при смене вкладки
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'accounts') {
      loadAccounts();
    }
  }, [activeTab]);

  const loadAccounts = async () => {
    try {
      console.log('🔄 Loading accounts...');
      const [manualData, plaidData] = await Promise.all([
        apiClient.getPaymentMethods(),
        apiClient.getPlaidAccounts(),
      ]);

      console.log('📊 Manual accounts:', manualData);
      console.log('🏦 Plaid data:', plaidData);

      setManualAccounts(manualData.payment_methods || []);
      setPlaidAccounts(plaidData.accounts || []);
      setPlaidItems(plaidData.items || []);
      
      console.log('✅ Accounts loaded:', {
        manual: manualData.payment_methods?.length || 0,
        plaid: plaidData.accounts?.length || 0,
        items: plaidData.items?.length || 0
      });
    } catch (error) {
      console.error('❌ Error loading accounts:', error);
      // Показываем ошибку пользователю
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
    }
  };

  const handleAddManualAccount = async () => {
    if (!newAccountName.trim()) return;

    try {
      setIsAddingManual(true);
      await apiClient.createPaymentMethod(newAccountName.trim());
      setNewAccountName('');
      setShowAddManualModal(false);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.addFailed'));
    } finally {
      setIsAddingManual(false);
    }
  };

  const handleDeleteManualAccount = async (accountId: string) => {
    if (!confirm(t('accounts.deleteAccountConfirmSimple'))) return;

    try {
      await apiClient.deletePaymentMethod(accountId);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.deleteFailed'));
    }
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      const account = manualAccounts.find(a => a.id === accountId);
      const isCurrentlyDefault = account?.is_default || false;
      await apiClient.setDefaultPaymentMethod(accountId, !isCurrentlyDefault);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.setDefaultFailed'));
    }
  };

  const handleEditBalance = async (accountId: string) => {
    console.log('🔄 [handleEditBalance] Called with accountId:', accountId);
    console.log('   Current editingBalanceId:', editingBalanceId);
    
    if (editingBalanceId !== accountId) {
      const account = manualAccounts.find(a => a.id === accountId);
      console.log('   Account found:', account);
      console.log('   Account balance:', account?.balance);
      setEditingBalanceId(accountId);
      setEditingBalanceValue(account?.balance?.toString() || '0');
      return;
    }

    try {
      const balance = parseFloat(editingBalanceValue) || 0;
      console.log('   Saving balance:', balance);
      await apiClient.updatePaymentMethodBalance(accountId, balance);
      setEditingBalanceId(null);
      setEditingBalanceValue('');
      await loadAccounts();
      console.log('✅ Balance updated successfully');
    } catch (error: any) {
      console.error('❌ Error updating balance:', error);
      alert(error.message || t('accounts.updateBalanceFailed'));
    }
  };

  const handleSyncTransactions = async (itemId: string) => {
    try {
      setSyncingItemId(itemId);
      await apiClient.syncPlaidTransactions(itemId);
      await loadAccounts();
      alert(t('accounts.syncSuccess'));
    } catch (error: any) {
      let errorMessage = 'Не удалось синхронизировать транзакции';
      
      // Используем улучшенную информацию от бэкенда
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log('Error data from backend:', errorData);
        
        // Используем suggestion от бэкенда, если есть
        if (errorData.suggestion) {
          errorMessage = `${errorData.error || error.message}\n\n${errorData.suggestion}`;
        } else if (errorData.error) {
          errorMessage = errorData.error;
          // Добавляем стандартное предложение для INVALID_ACCESS_TOKEN
          if (errorData.error_code === 'INVALID_ACCESS_TOKEN' || errorMessage.includes('Токен доступа недействителен')) {
            errorMessage += '\n\nТокен был создан для другого окружения. Пожалуйста, переподключите банк через Plaid Link.';
          }
        } else {
          errorMessage = error.message || errorMessage;
        }
      } else if (error.message?.includes('Item is not active') || error.message?.includes('Токен доступа недействителен')) {
        // Fallback для старых сообщений об ошибках
        errorMessage = 'Токен доступа недействителен. Токен был создан для другого окружения (sandbox/production).\n\nПожалуйста, переподключите банк через Plaid Link.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      alert(errorMessage);
    } finally {
      setSyncingItemId(null);
    }
  };

  const handleRemoveAccount = async (itemId: string, accountId: string) => {
    if (!confirm(t('accounts.deletePlaidAccountConfirm'))) {
      return;
    }

    try {
      setRemovingAccountId(accountId);
      setRemovingItemId(itemId);
      await apiClient.removePlaidAccount(itemId, accountId);
      await loadAccounts();
      alert(t('accounts.accountDeleted'));
    } catch (error: any) {
      alert(error.message || t('accounts.deleteFailed'));
    } finally {
      setRemovingAccountId(null);
      setRemovingItemId(null);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectingItemId) return;

    try {
      await apiClient.disconnectPlaidAccount(disconnectingItemId);
      setShowDisconnectModal(false);
      setDisconnectingItemId(null);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.disconnectFailed'));
    }
  };

  const handlePlaidSuccess = async () => {
    console.log('🔄 Refreshing accounts after Plaid connection...');
    // Небольшая задержка перед обновлением, чтобы сервер успел сохранить данные
    setTimeout(() => {
      loadAccounts();
    }, 1000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'transactions':
        return (
          <div className="operations-tab-content">
            <TransactionsScreen onExportClick={() => setShowExportModal(true)} />
          </div>
        );
      case 'analytics':
        return (
          <div className="operations-tab-content">
            <AnalyticsScreen />
          </div>
        );
      case 'recurring':
        return (
          <div className="operations-tab-content">
            <RecurringTransactionsScreen isEmbedded={true} />
          </div>
        );
      case 'categories':
        return (
          <div className="operations-tab-content">
            <CategoriesContent showHeader={false} />
          </div>
        );
      case 'limits':
        return (
          <div className="operations-tab-content">
            <LimitsScreen />
          </div>
        );
      case 'accounts':
        const currencySymbol = getCurrencySymbol(settings?.currency || 'USD');
        const accountsByInstitution = plaidAccounts.reduce((acc, account) => {
          const institution = account.institution_name;
          if (!acc[institution]) {
            acc[institution] = [];
          }
          acc[institution].push(account);
          return acc;
        }, {} as Record<string, any[]>);

        return (
          <div className="operations-tab-content">
            <div className="page">
              {/* Кнопка привязки банка */}
              <div className="section">
                <PremiumFeature featureId="plaid_bank_linking">
                  <PlaidLink
                    onSuccess={handlePlaidSuccess}
                    buttonText="Привязать банк"
                    className="plaid-link-button"
                  />
                </PremiumFeature>
              </div>

              {/* Банковские счета */}
              {plaidAccounts.length > 0 ? (
                <div className="section">
                  <h3 className="section__title">Банковские счета</h3>
                  {Object.entries(accountsByInstitution).map(([institution, accounts]) => {
                    const item = plaidItems.find((i: any) => i.institution_name === institution);
                    return (
                      <Card key={institution} className="bank-institution-card neo-static">
                        <div className="bank-institution-header">
                          <div className="bank-institution-info">
                            <Icon name="building" size={20} color="var(--color-primary)" />
                            <span className="bank-institution-name">{institution}</span>
                          </div>
                          {item && (
                            <div className="bank-institution-actions">
                              <Button
                                variant="ghost"
                                size="small"
                                onClick={() => handleSyncTransactions(item.item_id)}
                                loading={syncingItemId === item.item_id}
                                disabled={syncingItemId !== null}
                              >
                                <Icon name="refresh" size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="small"
                                onClick={() => {
                                  setDisconnectingItemId(item.item_id);
                                  setShowDisconnectModal(true);
                                }}
                              >
                                <Icon name="x" size={16} />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="bank-accounts-list">
                          {accounts.map((account: any) => (
                            <div key={account.account_id} className="bank-account-item">
                              <div className="account-info">
                                <div className="account-name-row">
                                  <span className="account-name">{account.name}</span>
                                  {account.mask && (
                                    <span className="account-mask">•••• {account.mask}</span>
                                  )}
                                </div>
                                <span className="account-type">
                                  {account.subtype || account.type}
                                </span>
                              </div>
                              <div className="account-balance-row">
                                <div className="account-balance">
                                  <span className="balance-amount">
                                    {(() => {
                                      // Для кредитных карт баланс должен быть отрицательным (долг)
                                      // В Plaid баланс кредитной карты положительный = долг
                                      const isCredit = account.type === 'credit' || account.subtype === 'credit card';
                                      const balance = isCredit 
                                        ? -Math.abs(account.balance_current) // Инвертируем для кредитных карт
                                        : account.balance_current; // Для депозитных счетов оставляем как есть
                                      return formatCurrency(balance, account.currency_code || 'USD');
                                    })()}
                                  </span>
                                  {account.balance_available !== account.balance_current && (
                                    <span className="balance-available">
                                      {(() => {
                                        const isCredit = account.type === 'credit' || account.subtype === 'credit card';
                                        const balance = isCredit 
                                          ? -Math.abs(account.balance_available)
                                          : account.balance_available;
                                        return formatCurrency(balance, account.currency_code || 'USD');
                                      })()}
                                    </span>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="small"
                                  className="neo-raised"
                                  onClick={() => handleRemoveAccount(item.item_id, account.account_id)}
                                  disabled={removingAccountId === account.account_id && removingItemId === item.item_id}
                                  title="Удалить этот счет"
                                >
                                  <Icon name="trash" size={16} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="section">
                  <Card padding="medium">
                    <div className="empty-state">
                      <Icon name="building" size={48} color="var(--color-text-secondary)" />
                      <p>Нет подключенных банков</p>
                      <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
                        Подключите банк, чтобы видеть балансы и транзакции
                      </p>
                    </div>
                  </Card>
                </div>
              )}

              {/* Ручные счета */}
              <div className="section">
                <div className="section-header">
                  <h3 className="section__title">Ручные счета</h3>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setShowAddManualModal(true)}
                  >
                    <Icon name="add" size={16} />
                  </Button>
                </div>
                {manualAccounts.length === 0 ? (
                  <Card>
                    <div className="empty-state">
                      <Icon name="credit_card" size={48} color="var(--color-text-secondary)" />
                      <p>Нет ручных счетов</p>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => setShowAddManualModal(true)}
                      >
                        Добавить счет
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <div className="manual-accounts-list">
                    {manualAccounts.map(account => (
                      <Card key={account.id} className="manual-account-item neo-static" padding="small">
                        <div className="manual-account-content">
                          <div className="account-info">
                            <span className="account-name">{account.name}</span>
                            <span className="account-type">{account.type}</span>
                          </div>
                          <div className="account-balance-row">
                            {editingBalanceId === account.id ? (
                              <div className="balance-edit-form">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingBalanceValue}
                                  onChange={(e) => setEditingBalanceValue(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleEditBalance(account.id);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingBalanceId(null);
                                      setEditingBalanceValue('');
                                    }
                                  }}
                                  className="balance-input"
                                  autoFocus
                                />
                                <Button
                                  variant="ghost"
                                  size="small"
                                  onClick={() => handleEditBalance(account.id)}
                                >
                                  <Icon name="check" size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="small"
                                  onClick={() => {
                                    setEditingBalanceId(null);
                                    setEditingBalanceValue('');
                                  }}
                                >
                                  <Icon name="x" size={16} />
                                </Button>
                              </div>
                            ) : (
                              <div className="account-balance-display" onClick={() => handleEditBalance(account.id)} title="Нажмите чтобы редактировать баланс">
                                <span className="balance-amount">
                                  {formatCurrency(account.balance ?? 0, settings?.currency || 'USD')}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditBalance(account.id);
                                  }}
                                  title="Редактировать баланс"
                                >
                                  <Icon name="edit" size={16} />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="account-actions">
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleSetDefault(account.id)}
                            title={account.is_default ? "Снять счет по умолчанию" : "Установить счет по умолчанию"}
                            className={account.is_default ? "default-account-btn default-account-btn--active" : "default-account-btn"}
                          >
                            <Icon name="star" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleDeleteManualAccount(account.id)}
                            title="Удалить счет"
                          >
                            <Icon name="trash" size={16} />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const showExportButton = activeTab === 'transactions';

  // Export handler function
  async function handleExport(config: ExportConfig) {
    try {
      // Формируем параметры для API
      let startDate: string | undefined;
      let endDate: string | undefined;
      let periodText = '';

      if (config.periodType === 'custom' && config.startDate && config.endDate) {
        startDate = config.startDate;
        endDate = config.endDate;
        periodText = `${new Date(startDate).toLocaleDateString('ru-RU')} - ${new Date(endDate).toLocaleDateString('ru-RU')}`;
      } else if (config.periodType === 'month' && config.selectedMonth && config.selectedYear) {
        const monthDate = new Date(config.selectedYear, config.selectedMonth - 1, 1);
        startDate = new Date(config.selectedYear, config.selectedMonth - 1, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, config.selectedMonth, 0).toISOString().split('T')[0];
        periodText = monthDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      } else if (config.periodType === 'quarter' && config.selectedQuarter && config.selectedYear) {
        const startMonth = (config.selectedQuarter - 1) * 3;
        startDate = new Date(config.selectedYear, startMonth, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, startMonth + 3, 0).toISOString().split('T')[0];
        periodText = `Q${config.selectedQuarter} ${config.selectedYear}`;
      } else if (config.periodType === 'year' && config.selectedYear) {
        startDate = new Date(config.selectedYear, 0, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, 11, 31).toISOString().split('T')[0];
        periodText = `${config.selectedYear} год`;
      } else if (config.periodType === 'all') {
        periodText = 'За всё время';
      }

      // Получаем данные транзакций
      const transactionsData = await apiClient.getTransactions(
        config.periodType === 'all' ? 'all' : undefined,
        startDate,
        endDate
      );

      // Получаем цели если нужно
      let goals;
      if (config.includeGoals) {
        const goalsData = await apiClient.getGoals();
        goals = goalsData.goals;
      }

      // Генерируем Excel файл
      const excelBlob = await generateExcelFile(
        {
          transactions: transactionsData.transactions,
          goals: goals,
          periodText: periodText,
          currency: settings?.currency || 'RUB',
          userName: userName
        },
        config
      );

      // Сохраняем файл на сервер
      const filename = `ALVEON_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      const formData = new FormData();
      formData.append('excel_file', excelBlob, filename);
      formData.append('filename', filename);
      formData.append('period_text', periodText);
      formData.append('export_config', JSON.stringify(config));
      formData.append('file_size_kb', Math.round(excelBlob.size / 1024).toString());

      await apiClient.uploadExport(formData);

      // Вибрация успеха
      if (window.Telegram?.WebApp?.HapticFeedback) {
        try {
          window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
        } catch (err) {
          console.warn('Haptic feedback not supported:', err);
        }
      }

      // Показываем успешное сообщение
      const tg = window.Telegram?.WebApp;
      const message = '✅ Экспорт завершен!\n\nФайл сохранен в разделе "Сохраненные таблицы".';
      
      // Проверяем версию API и наличие метода showPopup (доступен с версии 6.2)
      const isPopupSupported = tg && tg.version && parseFloat(tg.version) >= 6.2 && typeof tg.showPopup === 'function';
      
      if (isPopupSupported) {
        try {
          tg.showPopup({
            title: '✅ Экспорт завершен!',
            message: 'Файл сохранен в разделе "Сохраненные таблицы".\n\nВы можете скачать его в любое время.',
            buttons: [
              { id: 'close', type: 'cancel', text: 'Закрыть' }
            ]
          });
        } catch (err) {
          console.warn('showPopup failed, using showAlert:', err);
          if (tg && typeof tg.showAlert === 'function') {
            tg.showAlert(message);
          }
        }
      } else if (tg && typeof tg.showAlert === 'function') {
        tg.showAlert(message);
      } else {
        // Fallback для браузера
        alert(message);
      }
    } catch (error) {
      throw error;
    }
  }

  return (
    <div className="operations-screen">
      <Header title={t('operations.title')} />
      
      {/* Horizontal Tab Pills */}
      <div className="operations-tabs-container">
        <div className="operations-tabs-wrapper">
          <div className="operations-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`operations-tab ${activeTab === tab.id ? 'operations-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="operations-tab__icon">
                  <Icon name={tab.icon} size={24} />
                </div>
                <div className="operations-tab__label">
                  {t(tab.labelKey)}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="operations-content">
        {renderTabContent()}
      </div>

      {/* Floating Export Button */}
      {showExportButton && (
        <button className="operations-export-fab" onClick={() => setShowExportModal(true)}>
          <Icon name="download" size={24} />
        </button>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportFlow
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          currency={settings?.currency || 'RUB'}
        />
      )}

      {/* Модальное окно добавления ручного счета */}
      <Modal
        isOpen={showAddManualModal}
        onClose={() => {
          setShowAddManualModal(false);
          setNewAccountName('');
        }}
        title="Добавить счет"
      >
        <div className="add-manual-account-form">
          <input
            type="text"
            placeholder="Название счета (например: Наличные)"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddManualAccount();
              }
            }}
            autoFocus
          />
          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddManualModal(false);
                setNewAccountName('');
              }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleAddManualAccount}
              disabled={!newAccountName.trim()}
              loading={isAddingManual}
            >
              Добавить
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно отключения банка */}
      <Modal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setDisconnectingItemId(null);
        }}
        title="Отключить банк"
      >
        <div className="disconnect-modal-content">
          <p>Вы уверены, что хотите отключить этот банковский аккаунт?</p>
          <p className="disconnect-warning">
            Все транзакции из этого банка останутся в истории, но новые транзакции не будут синхронизироваться.
          </p>
          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDisconnectModal(false);
                setDisconnectingItemId(null);
              }}
            >
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
            >
              Отключить
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
